import { localClockOf, requestWindowLabel, windowLabel } from '@kronos/domain'
import type { DayAvailability } from '@kronos/domain'
import type { ComeInRequestView } from '../../data/schedule'
import { STATE_LABEL, shortWindow } from './shared'

/**
 * What one square of the availability calendar says: the hours when they can
 * come in, "Off" when not, nothing when they have not said — and a bar for
 * each request on that day, amber while it waits for an answer, green once it
 * is booked. Declined and cancelled requests are not drawn: a square only
 * shows what still has a claim on the day.
 */
export function AvailabilityCell({
  availability,
  requests,
  timeZone,
}: {
  availability: DayAvailability | null
  requests: readonly ComeInRequestView[]
  timeZone: string
}): JSX.Element {
  const open = requests.filter((r) => r.state === 'pending' || r.state === 'accepted')
  return (
    <>
      {availability?.window ? (
        <span className="avail-cell__hours">
          <span className="cal-narrow">{shortWindow(availability.window)}</span>
          <span className="cal-wide">{windowLabel(availability.window)}</span>
        </span>
      ) : availability ? (
        <span className="avail-cell__hours">Off</span>
      ) : null}
      {availability?.note ? <span className="avail-cell__note cal-wide">{availability.note}</span> : null}
      {open.map((r) => (
        <span key={r.id} className={`cal-mark cal-mark--${r.state}`}>
          {STATE_LABEL[r.state]} {shortWindow({ from: localClockOf(r.startsAt, timeZone), to: localClockOf(r.endsAt, timeZone) })}
        </span>
      ))}
    </>
  )
}

/** The spoken half of a square: "available 09:00 to 17:00, asked 09:00 to 15:00". */
export function describeAvailability(
  availability: DayAvailability | null,
  requests: readonly ComeInRequestView[],
  timeZone: string,
): string {
  const said = availability?.window
    ? `available ${availability.window.from} to ${availability.window.to}`
    : availability
      ? 'off'
      : 'not set'
  const note = availability?.note ? `, note: ${availability.note}` : ''
  const asked = requests
    .filter((r) => r.state === 'pending' || r.state === 'accepted')
    .map((r) => `${STATE_LABEL[r.state].toLowerCase()} ${requestWindowLabel(r, timeZone).replace('–', ' to ')}`)
  return [said + note, ...asked].join(', ')
}
